/**
 * Card Payment Redux Slice
 * =========================
 * Redux state management for card payment top-up functionality.
 *
 * Author: SuberPay Team
 * Date: 2026-09-14
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CardPaymentRequest } from '../../../types/bank-transfer.types'

export interface CardPaymentState {
  // Stripe configuration
  stripePublishableKey: string | null
  stripeConfigLoading: boolean

  // Payment requests
  payments: CardPaymentRequest[]
  paymentsLoading: boolean
  paymentsError: string | null

  // Current payment details
  currentPayment: CardPaymentRequest | null
  currentPaymentLoading: boolean

  // Payment Intent creation
  creatingPaymentIntent: boolean
  paymentIntentClientSecret: string | null
  paymentIntentId: string | null
  paymentReference: string | null

  // Payment confirmation
  confirmingPayment: boolean

  // Filters
  statusFilter: string | null
  currentPage: number
  pageSize: number
  totalCount: number

  // General error
  error: string | null
}

const initialState: CardPaymentState = {
  stripePublishableKey: null,
  stripeConfigLoading: false,

  payments: [],
  paymentsLoading: false,
  paymentsError: null,

  currentPayment: null,
  currentPaymentLoading: false,

  creatingPaymentIntent: false,
  paymentIntentClientSecret: null,
  paymentIntentId: null,
  paymentReference: null,

  confirmingPayment: false,

  statusFilter: null,
  currentPage: 1,
  pageSize: 20,
  totalCount: 0,

  error: null,
}

const cardPaymentSlice = createSlice({
  name: 'cardPayment',
  initialState,
  reducers: {
    // Stripe Config
    fetchStripeConfigStart(state) {
      state.stripeConfigLoading = true
      state.error = null
    },
    fetchStripeConfigSuccess(state, action: PayloadAction<string>) {
      state.stripePublishableKey = action.payload
      state.stripeConfigLoading = false
    },
    fetchStripeConfigFailure(state, action: PayloadAction<string>) {
      state.stripeConfigLoading = false
      state.error = action.payload
    },

    // Create Payment Intent
    createPaymentIntentStart(state) {
      state.creatingPaymentIntent = true
      state.error = null
      state.paymentIntentClientSecret = null
      state.paymentIntentId = null
      state.paymentReference = null
    },
    createPaymentIntentSuccess(
      state,
      action: PayloadAction<{
        clientSecret: string
        paymentIntentId: string
        reference: string
      }>
    ) {
      state.creatingPaymentIntent = false
      state.paymentIntentClientSecret = action.payload.clientSecret
      state.paymentIntentId = action.payload.paymentIntentId
      state.paymentReference = action.payload.reference
    },
    createPaymentIntentFailure(state, action: PayloadAction<string>) {
      state.creatingPaymentIntent = false
      state.error = action.payload
    },

    // Confirm Payment
    confirmPaymentStart(state) {
      state.confirmingPayment = true
      state.error = null
    },
    confirmPaymentSuccess(state, action: PayloadAction<CardPaymentRequest>) {
      state.confirmingPayment = false
      state.currentPayment = action.payload
      // Clear payment intent data
      state.paymentIntentClientSecret = null
      state.paymentIntentId = null
      state.paymentReference = null
    },
    confirmPaymentFailure(state, action: PayloadAction<string>) {
      state.confirmingPayment = false
      state.error = action.payload
    },

    // Fetch Payments
    fetchPaymentsStart(state) {
      state.paymentsLoading = true
      state.paymentsError = null
    },
    fetchPaymentsSuccess(
      state,
      action: PayloadAction<{
        payments: CardPaymentRequest[]
        totalCount: number
      }>
    ) {
      state.payments = action.payload.payments
      state.totalCount = action.payload.totalCount
      state.paymentsLoading = false
    },
    fetchPaymentsFailure(state, action: PayloadAction<string>) {
      state.paymentsLoading = false
      state.paymentsError = action.payload
    },

    // Fetch Payment Details
    fetchPaymentDetailsStart(state) {
      state.currentPaymentLoading = true
      state.error = null
    },
    fetchPaymentDetailsSuccess(state, action: PayloadAction<CardPaymentRequest>) {
      state.currentPayment = action.payload
      state.currentPaymentLoading = false
    },
    fetchPaymentDetailsFailure(state, action: PayloadAction<string>) {
      state.currentPaymentLoading = false
      state.error = action.payload
    },

    // Filters
    setStatusFilter(state, action: PayloadAction<string | null>) {
      state.statusFilter = action.payload
      state.currentPage = 1
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload
    },

    // Clear
    clearPaymentIntent(state) {
      state.paymentIntentClientSecret = null
      state.paymentIntentId = null
      state.paymentReference = null
    },
    clearCurrentPayment(state) {
      state.currentPayment = null
    },
    clearError(state) {
      state.error = null
    },
  },
})

export const {
  fetchStripeConfigStart,
  fetchStripeConfigSuccess,
  fetchStripeConfigFailure,

  createPaymentIntentStart,
  createPaymentIntentSuccess,
  createPaymentIntentFailure,

  confirmPaymentStart,
  confirmPaymentSuccess,
  confirmPaymentFailure,

  fetchPaymentsStart,
  fetchPaymentsSuccess,
  fetchPaymentsFailure,

  fetchPaymentDetailsStart,
  fetchPaymentDetailsSuccess,
  fetchPaymentDetailsFailure,

  setStatusFilter,
  setCurrentPage,

  clearPaymentIntent,
  clearCurrentPayment,
  clearError,
} = cardPaymentSlice.actions

export default cardPaymentSlice.reducer
